import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Shield, FileCheck, ArrowRight, Search, CheckCircle2, AlertTriangle,
  TrendingUp, Heart, Zap, Lock, BookOpen, Users, Calendar, MapPin, Play,
} from "lucide-react";
import Reveal from "../components/Reveal";
import Logo from "../components/Logo";
import { API } from "../lib/utils";

const scamTypes = [
  { icon: TrendingUp, title: "Cryptocurrency Scams", desc: "Bitcoin, trading platforms, fake exchanges" },
  { icon: Heart, title: "Romance Scams", desc: "Emotional manipulation, fake relationships" },
  { icon: Zap, title: "Investment Fraud", desc: "Ponzi schemes, fake brokers, forex" },
  { icon: Lock, title: "Recovery Scams", desc: "Fake recovery services targeting victims" },
];

const principles = [
  { icon: Heart, title: "Empathy First", desc: "We understand the emotional toll. No judgment, just support." },
  { icon: BookOpen, title: "Education Before Action", desc: "We help you understand your situation before any decisions." },
  { icon: Shield, title: "Honest Guidance", desc: "We'll tell you the truth about your recovery prospects." },
  { icon: Users, title: "No Pressure", desc: "Take your time. We're here when you're ready." },
];

const cases = [
  {
    icon: TrendingUp, tag: "Cryptocurrency Investment Fraud",
    quote: "A professional investor was drawn into what appeared to be a legitimate crypto trading platform through a LinkedIn connection. The platform showed impressive returns and allowed small withdrawals initially—a classic tactic to build trust before requesting larger deposits.",
    time: "4 months from first contact to resolution", place: "United States",
    outcome: "Partial recovery through bank wire dispute process",
    factors: ["Acted within 30 days of realizing the fraud", "Documented all communications and transactions", "Bank wire transfers (more recoverable than pure crypto)"],
    insight: "The initial small withdrawal was designed to build false confidence. Legitimate platforms don't need to 'prove' they can pay out.",
  },
  {
    icon: Heart, tag: "Romance Scam",
    quote: "Over 8 months, a widow developed what felt like a genuine relationship with someone claiming to be an overseas engineer. Requests started small—help with a customs fee—then escalated to medical emergencies and investment 'opportunities' for their future together.",
    time: "6 months of recovery work after reporting", place: "Canada",
    outcome: "Full documentation package prepared for law enforcement investigation",
    factors: ["Preserved all messages across platforms", "Filed comprehensive police report", "Connected with support group for emotional recovery"],
    insight: "The emotional manipulation was sophisticated and gradual. Anyone can be vulnerable when targeted with patience and psychological skill.",
  },
  {
    icon: Zap, tag: "Forex Trading Fraud",
    quote: "An experienced day trader was recruited by an unregulated 'broker' offering leverage and mentorship. The platform was entirely fabricated—trades were simulated, profits were fictional, and withdrawal requests triggered escalating 'tax' and 'fee' demands.",
    time: "5 months total recovery process", place: "United Kingdom",
    outcome: "Successful chargeback for credit card portion of deposits",
    factors: ["Credit card used for initial deposits (crucial for recovery)", "Broker was unregistered with financial authorities", "Platform screenshots proved manipulation"],
    insight: "Always verify broker registration before depositing. Unregulated platforms operate outside legal frameworks that protect investors.",
  },
];

const DEFAULT_TESTIMONIALS = [
  { poster_url: "/testimonials/t1.jpg", video_url: "", quote: "After losing a significant amount to a crypto trading scam, the team guided me through every step of the recovery process. I couldn't have done it alone.", name: "James R." },
  { poster_url: "/testimonials/t2.jpg", video_url: "", quote: "I was devastated after falling victim to an investment fraud. Their honest guidance helped me understand my options and take action quickly.", name: "Sarah M." },
  { poster_url: "/testimonials/t3.jpg", video_url: "", quote: "The empathy and professionalism I experienced gave me hope during one of the hardest times of my life. Truly grateful for the support.", name: "Linda K." },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) setTestimonials(data);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="flex flex-col" data-testid="home-page">
      {/* HERO */}
      <section className="hero-mesh relative overflow-hidden">
        <div className="pointer-events-none absolute -right-10 -top-10 h-[520px] w-[520px] animate-gradient-shift rounded-full bg-secondary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-[560px] w-[560px] animate-gradient-shift rounded-full bg-safe/10 blur-3xl" />
        <div
          className="pointer-events-none absolute -bottom-16 right-0 select-none font-display font-extrabold leading-none text-primary/5"
          style={{ fontSize: "clamp(160px, 26vw, 360px)" }}
          aria-hidden
        >
          RL
        </div>
        <div className="container relative grid grid-cols-1 gap-12 py-20 md:py-28 lg:grid-cols-12 lg:gap-16">
          <div className="order-2 space-y-8 lg:order-1 lg:col-span-4" data-testid="hero-principles">
            <div className="flex items-center gap-3">
              <span className="font-display text-[11px] font-bold uppercase tracking-[0.35em] text-secondary">Our Principles</span>
            </div>
            <div className="h-px w-12 bg-secondary" />
            <div className="space-y-8">
              {[
                { n: "01", title: "Empathy First", desc: "We understand the emotional toll. No judgment, just support." },
                { n: "02", title: "Education Before Action", desc: "We help you understand your situation before any decisions." },
                { n: "03", title: "Honest Guidance", desc: "We'll tell you the truth about your recovery prospects." },
              ].map((p, i) => (
                <motion.div
                  key={p.n}
                  className="flex items-start gap-4"
                  initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 + i * 0.15 }}
                >
                  <span className="font-display text-4xl font-extrabold leading-none text-primary/10">{p.n}</span>
                  <div className="pt-1">
                    <h3 className="font-display text-lg font-semibold text-primary">{p.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{p.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="flex items-center gap-2 border-t border-primary/10 pt-8 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              <Shield className="h-4 w-4 text-secondary" /> Trusted Recovery Guidance
            </div>
          </div>

          <div className="order-1 space-y-8 lg:order-2 lg:col-span-8 lg:border-l lg:border-accent/20 lg:pl-10">
            <motion.div
              className="flex items-center gap-3"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
            >
              <div className="h-px w-10 bg-accent" />
              <span className="font-display text-[11px] font-bold uppercase tracking-[0.35em] text-accent">
                Investment Fraud Recovery
              </span>
            </motion.div>
            <motion.h1
              className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-primary sm:text-5xl lg:text-6xl xl:text-7xl"
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            >
              Lost Money to an <span className="text-secondary">Investment Scam?</span>
              <span className="mt-3 block font-display text-3xl font-normal italic text-accent sm:text-4xl lg:text-5xl xl:text-6xl">
                We're Here to Help.
              </span>
            </motion.h1>
            <motion.p
              className="max-w-2xl text-lg font-light leading-relaxed text-primary/75 lg:text-xl"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            >
              You're not alone, and it's not your fault. Our free consultation helps you understand
              what happened, what your options are, and what realistic recovery might look like.
            </motion.p>
            <motion.div
              className="flex flex-wrap gap-4 pt-2"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
            >
              <Link to="/contact" className="btn-primary" data-testid="hero-contact-btn">
                <Search className="h-5 w-5" /> Contact Our Team
              </Link>
              <Link to="/how-it-works" className="btn-outline" data-testid="hero-how-btn">
                Learn How Recovery Works <ArrowRight className="h-5 w-5" />
              </Link>
            </motion.div>
            <div className="mt-2 grid grid-cols-1 gap-5 border-t border-primary/10 pt-10 sm:grid-cols-2">
              {[
                { icon: Shield, label: "Secure & Confidential" },
                { icon: FileCheck, label: "No Upfront Fees" },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent/15 text-accent">
                    <b.icon className="h-4 w-4" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary/75">{b.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE ARE / ARE NOT */}
      <section className="section bg-muted/30">
        <div className="container grid max-w-4xl gap-8 md:grid-cols-2">
          <Reveal>
            <div className="calm-card h-full hover-lift" data-testid="what-we-are">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-safe-light">
                  <CheckCircle2 className="h-5 w-5 text-safe" />
                </div>
                <h3 className="font-display text-xl font-semibold">What We Are</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                {["An educational resource to help you understand your situation", "A tool to assess your case and explore options", "A guide to legitimate next steps and resources", "Honest about recovery limitations and realistic outcomes"].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-safe" /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="calm-card h-full border-destructive/20 bg-destructive/5 hover-lift" data-testid="what-we-are-not">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="font-display text-xl font-semibold">What We Are Not</h3>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                {["A guarantee of money recovery (no one can promise this)", "A service that will pressure you into quick decisions", "A company that charges upfront fees for consultations", "A replacement for legal or financial advice"].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <AlertTriangle className="mt-1 h-4 w-4 shrink-0 text-destructive" /> <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SCAM TYPES */}
      <section className="section">
        <div className="container">
          <Reveal className="mb-12 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold md:text-4xl">Investment Scam Types We Help With</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Our free consultation identifies your specific fraud type and provides tailored recovery
              guidance for cryptocurrency, romance, and investment scams.
            </p>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {scamTypes.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <Link to="/insights" className="calm-card group relative block h-full overflow-hidden hover-lift" data-testid={`scam-type-${i}`}>
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-trust-light transition-colors group-hover:bg-secondary/20">
                    <s.icon className="h-6 w-6 text-trust" />
                  </div>
                  <h3 className="mb-2 font-semibold transition-colors group-hover:text-secondary">{s.title}</h3>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                  <ArrowRight className="absolute bottom-6 right-6 h-5 w-5 translate-x-2 text-secondary opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/insights" className="btn-teal" data-testid="all-scam-types-btn">
              Learn About All Investment Scam Types <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="section bg-trust-light/60">
        <div className="container">
          <Reveal className="mb-12 text-center">
            <h2 className="mb-4 font-display text-3xl font-bold md:text-4xl">Our Guiding Principles</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              We believe recovery starts with understanding and trust. Here's how we approach helping you.
            </p>
          </Reveal>
          <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="rounded-2xl bg-white/60 p-6 text-center backdrop-blur-sm hover-lift hover:bg-white hover:shadow-elevated">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary/10">
                    <p.icon className="h-7 w-7 text-secondary" />
                  </div>
                  <h3 className="mb-2 font-semibold">{p.title}</h3>
                  <p className="text-sm text-muted-foreground">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="section bg-muted/20">
        <div className="container">
          <Reveal className="mb-12 text-center">
            <div className="trust-badge mx-auto mb-4"><FileCheck className="h-4 w-4" /> Investment Fraud Recovery Examples</div>
            <h2 className="mb-4 font-display text-3xl font-bold md:text-4xl">Real Scam Recovery Journeys</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              These anonymized case patterns represent actual investment fraud recovery journeys. Every
              case is unique—these examples demonstrate realistic outcomes, not guarantees.
            </p>
          </Reveal>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-3">
            {cases.map((c, i) => (
              <Reveal key={c.tag} delay={i * 0.1}>
                <div className="calm-card h-full hover-lift" data-testid={`case-study-${i}`}>
                  <div className="mb-4 flex items-center gap-2">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-trust-light">
                      <c.icon className="h-5 w-5 text-trust" />
                    </div>
                    <span className="text-sm font-medium text-trust">{c.tag}</span>
                  </div>
                  <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{c.quote}</p>
                  <div className="mb-4 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.time}</span>
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.place}</span>
                  </div>
                  <div className="mb-4 rounded-lg border border-safe/20 bg-safe-light/50 p-3">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-safe" />
                      <span className="text-sm font-medium text-primary">{c.outcome}</span>
                    </div>
                  </div>
                  <div className="mb-4 space-y-1.5">
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Key Factors:</span>
                    <div className="flex flex-wrap gap-1.5">
                      {c.factors.map((f) => (
                        <span key={f} className="rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground">{f}</span>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-lg border border-secondary/10 bg-calm-light/40 p-3">
                    <p className="text-xs text-muted-foreground"><strong className="text-secondary">Key insight:</strong> {c.insight}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mx-auto mt-8 max-w-2xl">
            <div className="gentle-alert text-center">
              <p className="text-sm text-muted-foreground">
                <strong>Important:</strong> These patterns are for educational purposes. Recovery outcomes
                depend on many factors including timing, payment method, jurisdiction, and available
                evidence. Past patterns do not guarantee future results.
              </p>
            </div>
          </Reveal>
          <div className="mt-8 text-center">
            <Link to="/insights" className="btn-teal" data-testid="more-insights-btn">
              Explore More Case Insights <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* VIDEO TESTIMONIALS */}
      <section className="section">
        <div className="container">
          <Reveal className="mb-12 text-center">
            <div className="trust-badge mx-auto mb-4"><Users className="h-4 w-4" /> Client Testimonials</div>
            <h2 className="mb-4 font-display text-3xl font-bold md:text-4xl">Hear From Those We've Helped</h2>
            <p className="mx-auto max-w-2xl text-muted-foreground">
              Real stories from real people who trusted us to guide them through the recovery process.
            </p>
          </Reveal>
          <div className="mx-auto grid max-w-6xl gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.id || t.name} delay={(i % 3) * 0.08}>
                <div className="calm-card h-full hover-lift" data-testid={`testimonial-${i}`}>
                  <VideoCard poster={t.poster_url} videoUrl={t.video_url} name={t.name} />
                  <p className="text-sm leading-relaxed text-muted-foreground">{t.quote}</p>
                  <p className="mt-3 text-xs font-medium text-trust">— {t.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="section">
        <div className="container">
          <Reveal className="mx-auto max-w-3xl">
            <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-gradient-to-br from-calm-light/60 via-white to-trust-light/50 p-10 text-center shadow-soft md:p-14">
              <div className="mb-6 flex justify-center"><Logo size="lg" /></div>
              <h2 className="mb-4 font-display text-3xl font-bold md:text-4xl">Get Confidential Guidance Today</h2>
              <p className="mx-auto mb-8 max-w-xl text-muted-foreground">
                Reach out to our team for a private, no-pressure conversation about your situation and
                the recovery options available to you.
              </p>
              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <Link to="/contact" className="btn-primary" data-testid="cta-contact-btn"><Search className="h-5 w-5" /> Contact Us</Link>
                <Link to="/how-it-works" className="btn-outline">See How It Works</Link>
              </div>
              <p className="mt-6 text-xs text-muted-foreground">No spam. Your information stays private and secure.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function VideoCard({ poster, videoUrl, name }) {
  const [playing, setPlaying] = React.useState(false);
  const fallback = "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=faces&fit=crop&w=1200&h=675&q=80";
  const src = videoUrl && videoUrl.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${videoUrl}` : videoUrl;
  return (
    <div className="relative mb-4 aspect-video overflow-hidden rounded-xl bg-muted">
      {playing && src ? (
        <video className="h-full w-full object-cover" controls autoPlay data-testid="testimonial-video">
          <source src={src} type="video/mp4" />
        </video>
      ) : (
        <button
          className="group h-full w-full"
          onClick={() => src && setPlaying(true)}
          data-testid="testimonial-play"
          aria-label={src ? `Play ${name}'s testimonial` : `${name} testimonial`}
        >
          <img src={poster || fallback} alt={`${name} testimonial`} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ objectPosition: "center 30%" }} />
          <span className={`absolute inset-0 flex items-center justify-center transition-colors ${src ? "bg-primary/25 group-hover:bg-primary/35" : "bg-primary/10"}`}>
            {src && (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-primary backdrop-blur-sm transition-transform group-hover:scale-110">
                <Play className="ml-1 h-7 w-7" fill="currentColor" />
              </span>
            )}
          </span>
        </button>
      )}
    </div>
  );
}
